import {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {useLocation, useNavigate} from "react-router-dom";
import Banner2 from "../components/Banner2.jsx";
import Button from "../components/Button.jsx";
import LoadingComponent from "../components/LoadingComponent.jsx";
import useDownloadState from "../api/utilities/useDownloadState.js";
import {GlobalContext} from "../utils/context/GlobalContextProvider.jsx";
import {AuthenticationApi, ProjectApi, ProjectIntegrationsApi} from "../api/authority/generated/index";
import {useErrorTranslation} from "../api/utilities/translateErrorMessage.js";
import {useBrands} from "../utils/brands/branding.js";
import moment from "moment-timezone";
import {hiddenAlertIdsForPath, isAdministrationPath, visibleAlertsExcludingIds, visibleProjectAlerts} from "./appAlertsVisibility.js";
import {FilterContext} from "../components/filters/DashboardFilterContextProvider.jsx";
import {projectIntegrationStatusProjection, usePublishProjectIntegrationStatus} from "./ProjectIntegrationStatusContext.jsx";

function hasReportingPeriodFilter(pathname) {
    const match = pathname.match(/^\/project\/[^/]+\/(reporting|reporting-lab|order)(?:\/([^/]+))?(?:\/|$)/);
    if (!match) {
        return false;
    }

    const [, section, page] = match;
    if (section === 'reporting' || section === 'reporting-lab') {
        return !['merchant-catalogue', 'google-source-ownership'].includes(page);
    }

    return ['performance-summary', 'marketing-and-sales', 'sales-promotions', 'campaign-performance', 'campaign-profitability', 'campaign-comparison'].includes(page);
}

export default function AppAlerts({alerts = [], className = '', hiddenAlertIds = [], compactTopSpacing = false, bannerRadiusClassName, periodReport = false, comparisonRetentionExpired = false}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const brands = useBrands();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const dashboardFilters = useContext(FilterContext);
    const publishProjectIntegrationStatus = usePublishProjectIntegrationStatus();
    const location = useLocation();
    const navigate = useNavigate();
    const [state, setDownloading] = useDownloadState();
    const [buttonPressed, setButtonPressed] = useState(false);
    const [projectIntegrations, setProjectIntegrations] = useState(null);
    const [projectRuntime, setProjectRuntime] = useState(null);
    const [projectIntegrationRuntimeStatuses, setProjectIntegrationRuntimeStatuses] = useState(null);
    const [orderStatusSettings, setOrderStatusSettings] = useState(null);
    const administrationPage = isAdministrationPath(location.pathname);

    const currentProject = useMemo(() => {
        return context.currentUserProjects?.find((project) => project.id === context.currentProjectId) ?? null;
    }, [context.currentProjectId, context.currentUserProjects]);

    const marketingIntegrationNames = useMemo(() => (
        new Set(
            Object.entries(brands)
                .filter(([, brand]) => brand?.isAdSystem === true)
                .map(([integrationName]) => integrationName)
        )
    ), [brands]);

    const hasMarketingIntegration = useMemo(() => {
        if (!Array.isArray(projectIntegrations)) {
            return false;
        }

        return projectIntegrations.some((integration) => marketingIntegrationNames.has(integration?.name));
    }, [marketingIntegrationNames, projectIntegrations]);

    useEffect(() => {
        if (!buttonPressed) {
            return;
        }

        setButtonPressed(false);
        setDownloading(true);

        new AuthenticationApi(getAuthorityConfiguration())
            .resendConfirmationLink({
                resendConfirmEmailInput: {
                    email: context.currentIdentity?.user?.email ?? '',
                },
            })
            .then(() => {
                toast.success(t('user.confirmation-email-sent'));
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setDownloading(false));
    }, [buttonPressed, context.currentIdentity?.user?.email, et, getAuthorityConfiguration, setDownloading, t]);

    useEffect(() => {
        if (administrationPage || !currentProject?.id || currentProject.hasErpIntegration !== true) {
            publishProjectIntegrationStatus({
                projectId: currentProject?.id ?? null,
                pending: false,
                integrations: null,
            });
            setProjectIntegrations(null);
            setProjectRuntime(null);
            setProjectIntegrationRuntimeStatuses(null);
            setOrderStatusSettings(null);
            return;
        }

        let cancelled = false;

        const authorityConfiguration = getAuthorityConfiguration();
        publishProjectIntegrationStatus({projectId: currentProject.id, pending: true, integrations: null});

        Promise.allSettled([
            new ProjectIntegrationsApi(authorityConfiguration).listProjectIntegrations({projectId: currentProject.id}),
            new ProjectApi(authorityConfiguration).projectStatus({projectId: currentProject.id}),
            new ProjectApi(authorityConfiguration).orderStatusList({projectId: currentProject.id}),
        ])
            .then(([integrationsResult, runtimeOverviewResult, orderStatusSettingsResult]) => {
                if (cancelled) {
                    return;
                }

                if (integrationsResult.status === 'fulfilled') {
                    setProjectIntegrations(integrationsResult.value);
                    publishProjectIntegrationStatus({
                        projectId: currentProject.id,
                        pending: false,
                        integrations: projectIntegrationStatusProjection(integrationsResult.value),
                    });
                } else {
                    setProjectIntegrations(null);
                    publishProjectIntegrationStatus({projectId: currentProject.id, pending: false, integrations: null});
                }

                setProjectRuntime(
                    runtimeOverviewResult.status === 'fulfilled'
                        ? runtimeOverviewResult.value?.projectRuntime ?? null
                        : null
                );
                setProjectIntegrationRuntimeStatuses(
                    runtimeOverviewResult.status === 'fulfilled'
                        ? runtimeOverviewResult.value?.integrations ?? null
                        : null
                );

                setOrderStatusSettings(
                    orderStatusSettingsResult.status === 'fulfilled'
                        ? orderStatusSettingsResult.value
                        : null
                );
            });

        return () => {
            cancelled = true;
        };
    }, [administrationPage, currentProject?.hasErpIntegration, currentProject?.id, getAuthorityConfiguration, publishProjectIntegrationStatus]);

    const hasAddonWaitingForFirstSync = useMemo(() => {
        if (!currentProject?.id || !Array.isArray(projectIntegrationRuntimeStatuses)) {
            return false;
        }

        return projectIntegrationRuntimeStatuses.some((integration) => (
            ['UpgatesAddon', 'ShoptetAddon'].includes(integration?.integrationName)
            && integration?.bootstrapPending === true
        ));
    }, [currentProject?.id, projectIntegrationRuntimeStatuses]);

    useEffect(() => {
        if (administrationPage || !hasAddonWaitingForFirstSync || !currentProject?.id) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            new ProjectApi(getAuthorityConfiguration())
                .projectStatus({projectId: currentProject.id})
                .then((runtimeOverview) => {
                    setProjectRuntime(runtimeOverview?.projectRuntime ?? null);
                    setProjectIntegrationRuntimeStatuses(runtimeOverview?.integrations ?? null);
                })
                .catch(() => {});
        }, 15000);

        return () => clearInterval(intervalId);
    }, [administrationPage, currentProject?.id, getAuthorityConfiguration, hasAddonWaitingForFirstSync]);

    const addonBootstrapAlert = useMemo(() => {
        if (!hasAddonWaitingForFirstSync || hasMarketingIntegration) {
            return null;
        }

        const integrationsPath = `/project/${currentProject.id}/settings/integrations`;
        const subscriptionPath = `/project/${currentProject.id}/settings/subscription`;

        return {
            id: 'addon-bootstrap-first-sync',
            type: 'info',
            showClose: false,
            hiddenOnPaths: [integrationsPath, subscriptionPath],
            content: (
                <span>
                    {t('app-alerts.addon-bootstrap-pending.message')}
                </span>
            ),
            action: (
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(integrationsPath)}
                >
                    {t('app-alerts.addon-bootstrap-pending.action')}
                </Button>
            ),
        };
    }, [currentProject?.id, hasAddonWaitingForFirstSync, hasMarketingIntegration, navigate, t]);

    const missingMarketingIntegrationsAlert = useMemo(() => {
        if (!currentProject?.id || !Array.isArray(projectIntegrations)) {
            return null;
        }

        if (hasMarketingIntegration || hasAddonWaitingForFirstSync) {
            return null;
        }

        const integrationsPath = `/project/${currentProject.id}/settings/integrations`;
        const subscriptionPath = `/project/${currentProject.id}/settings/subscription`;

        return {
            id: 'missing-marketing-integrations',
            type: 'info',
            showClose: false,
            hiddenOnPaths: [integrationsPath, subscriptionPath],
            title: t('app-alerts.missing-marketing-integrations.title', {defaultValue: 'Chybí marketingové integrace'}),
            content: (
                <span>
                    {t('app-alerts.missing-marketing-integrations.message')}
                </span>
            ),
            action: (
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(integrationsPath)}
                >
                    {t('app-alerts.missing-marketing-integrations.action')}
                </Button>
            ),
        };
    }, [currentProject?.id, hasAddonWaitingForFirstSync, hasMarketingIntegration, navigate, projectIntegrations, t]);

    const missingCompletedOrderStatusesAlert = useMemo(() => {
        if (!currentProject?.id || !orderStatusSettings) {
            return null;
        }

        const hasDownloadedOrderStatuses = (orderStatusSettings.all?.length ?? 0) > 0;
        const hasCompletedOrderStatuses = (orderStatusSettings.completed?.length ?? 0) > 0;

        if (!hasDownloadedOrderStatuses || hasCompletedOrderStatuses) {
            return null;
        }

        const orderStatusesPath = `/project/${currentProject.id}/settings/order-statuses`;

        return {
            id: 'missing-completed-order-statuses',
            type: 'info',
            showClose: false,
            hiddenOnPaths: [orderStatusesPath],
            content: (
                <span>
                    {t('app-alerts.missing-completed-order-statuses.message')}
                </span>
            ),
            action: (
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(orderStatusesPath)}
                >
                    {t('app-alerts.missing-completed-order-statuses.action')}
                </Button>
            ),
        };
    }, [currentProject?.id, navigate, orderStatusSettings, t]);

    const missingSubscriptionAlert = useMemo(() => {
        if (!currentProject?.id || currentProject.subscription) {
            return null;
        }

        const subscriptionPath = `/project/${currentProject.id}/settings/subscription`;
        const mayManageBilling = currentProject.isOwner === true
            || currentProject.permissions?.includes('ProjectBilling');

        return {
            id: 'missing-subscription',
            type: 'danger',
            showClose: false,
            hiddenOnPaths: [subscriptionPath],
            title: t('app-alerts.missing-subscription.title', {defaultValue: 'Projekt nemá aktivní balíček'}),
            content: mayManageBilling
                ? t('app-alerts.missing-subscription.message', {defaultValue: 'Dokud balíček nevyberete a nenastavíte platební kartu, data se nebudou synchronizovat.'})
                : t('app-alerts.missing-subscription.no-permission', {defaultValue: 'Data se nesynchronizují. Kontaktujte vlastníka projektu nebo uživatele s přístupem k fakturaci.'}),
            action: mayManageBilling ? (
                <Button variant="outline-red" onClick={() => navigate(subscriptionPath)}>
                    {t('app-alerts.missing-subscription.action', {defaultValue: 'Vybrat balíček'})}
                </Button>
            ) : null,
        };
    }, [currentProject, navigate, t]);

    const subscriptionPaymentAlert = useMemo(() => {
        if (currentProject?.subscription?.status !== 'past_due') {
            return null;
        }

        const subscriptionPath = `/project/${currentProject.id}/settings/subscription`;
        const graceEndsAt = currentProject.subscription.paymentGraceEndsAt
            ? moment(currentProject.subscription.paymentGraceEndsAt).format('DD.MM.YYYY')
            : null;
        const mayManageBilling = currentProject.isOwner === true
            || currentProject.permissions?.includes('ProjectBilling');
        return {
            id: 'subscription-payment-past-due',
            type: 'warning',
            showClose: false,
            hiddenOnPaths: [subscriptionPath],
            content: graceEndsAt
                ? t('app-alerts.subscription-payment-past-due.message-with-date', {defaultValue: 'Platbu za balíček se nepodařilo provést. Synchronizace pokračuje do {{date}}; zkontrolujte prosím platební kartu.', date: graceEndsAt})
                : t('app-alerts.subscription-payment-past-due.message', {defaultValue: 'Platbu za balíček se nepodařilo provést. Zkontrolujte prosím platební kartu.'}),
            action: mayManageBilling ? (
                <Button variant="outline-warning" onClick={() => navigate(subscriptionPath)}>
                    {t('app-alerts.subscription-payment-past-due.action', {defaultValue: 'Zkontrolovat platbu'})}
                </Button>
            ) : null,
        };
    }, [currentProject, navigate, t]);

    const retentionAlert = useMemo(() => {
        const retentionMonths = currentProject?.subscription?.currentPackage?.retentionMonths ?? 0;
        const contextRangeAllowed = periodReport || hasReportingPeriodFilter(location.pathname);
        const contextSinceIsValid = contextRangeAllowed
            && moment.isMoment(dashboardFilters?.since)
            && dashboardFilters.since.isValid();
        const selectedSince = contextSinceIsValid
            ? dashboardFilters.since.format('YYYY-MM-DD')
            : new URLSearchParams(location.search).get('since');
        const selectedUntil = contextSinceIsValid
            ? moment.isMoment(dashboardFilters?.until) && dashboardFilters.until.isValid()
                ? dashboardFilters.until.format('YYYY-MM-DD')
                : null
            : new URLSearchParams(location.search).get('until');
        if (!currentProject?.id || retentionMonths <= 0 || !selectedSince) {
            return null;
        }

        const oldestAccessibleDate = moment().subtract(retentionMonths, 'months').startOf('day');
        const selectedSinceDate = moment(selectedSince, 'YYYY-MM-DD', true);
        const mainPeriodOutsideRetention = selectedSinceDate.isValid()
            && selectedSinceDate.isBefore(oldestAccessibleDate, 'day');
        if (!mainPeriodOutsideRetention && !comparisonRetentionExpired) {
            return null;
        }
        const selectedUntilDate = selectedUntil ? moment(selectedUntil, 'YYYY-MM-DD', true) : null;
        const selectedRangeIsValid = selectedUntilDate?.isValid()
            && !selectedUntilDate.isBefore(selectedSinceDate, 'day');
        const fullyExpired = mainPeriodOutsideRetention && selectedRangeIsValid
            && selectedUntilDate.isBefore(oldestAccessibleDate, 'day');

        const subscriptionPath = `/project/${currentProject.id}/settings/subscription`;
        const mayManageBilling = currentProject.isOwner === true
            || currentProject.permissions?.includes('ProjectBilling');
        return {
            id: 'subscription-retention',
            type: 'warning',
            showClose: false,
            hiddenOnPaths: [subscriptionPath],
            content: comparisonRetentionExpired && !mainPeriodOutsideRetention
                ? t('app-alerts.subscription-retention.message-comparison-expired', {
                    defaultValue: 'Část porovnávaných období přesahuje historii dostupnou v tomto balíčku. Data lze zobrazit od {{date}}.',
                    date: oldestAccessibleDate.format('DD.MM.YYYY'),
                })
                : fullyExpired
                    ? t('app-alerts.subscription-retention.message-fully-expired', {
                        defaultValue: 'Zvolené období je mimo historii dostupnou v tomto balíčku. Data lze zobrazit od {{date}}.',
                        date: oldestAccessibleDate.format('DD.MM.YYYY'),
                    })
                    : t('app-alerts.subscription-retention.message', {
                        defaultValue: 'Zvolený rozsah přesahuje historii dostupnou v tomto balíčku. Data jsou zobrazena od {{date}}.',
                        date: oldestAccessibleDate.format('DD.MM.YYYY'),
                    }),
            action: mayManageBilling ? (
                <Button variant="outline-warning" onClick={() => navigate(subscriptionPath)}>
                    {t('app-alerts.subscription-retention.action', {defaultValue: 'Změnit balíček'})}
                </Button>
            ) : null,
        };
    }, [currentProject, comparisonRetentionExpired, dashboardFilters?.since, dashboardFilters?.until, location.pathname, location.search, navigate, periodReport, t]);

    const globalAlerts = useMemo(() => {
        const configuredGlobalAlerts = context.globalAlerts ?? [];
        const dynamicGlobalAlerts = visibleProjectAlerts(location.pathname, [
            addonBootstrapAlert,
            missingSubscriptionAlert,
            subscriptionPaymentAlert,
            retentionAlert,
            missingMarketingIntegrationsAlert,
            missingCompletedOrderStatusesAlert,
        ]);

        if (context.currentIdentity === null || context.currentIdentity.user?.emailConfirmed === true) {
            return [...dynamicGlobalAlerts, ...configuredGlobalAlerts];
        }

        return [
            {
                id: 'confirm-email',
                type: 'warning',
                showClose: false,
                content: t('user.email-not-confirmed'),
                action: (
                    <LoadingComponent loading={state.downloading}>
                        <Button variant="outline-warning" onClick={() => setButtonPressed(true)}>
                            {t('user.resend-confirmation-email')}
                        </Button>
                    </LoadingComponent>
                ),
            },
            ...dynamicGlobalAlerts,
            ...configuredGlobalAlerts,
        ];
    }, [
        addonBootstrapAlert,
        context.currentIdentity,
        context.globalAlerts,
        location.pathname,
        missingCompletedOrderStatusesAlert,
        missingSubscriptionAlert,
        missingMarketingIntegrationsAlert,
        retentionAlert,
        state.downloading,
        subscriptionPaymentAlert,
        t,
    ]);

    const pathHiddenAlertIds = [...hiddenAlertIds, ...hiddenAlertIdsForPath(location.pathname)];
    const allAlerts = visibleAlertsExcludingIds([...globalAlerts, ...alerts], pathHiddenAlertIds).filter((alert) => {

        const hiddenOnPaths = alert.hiddenOnPaths ?? [];

        return !hiddenOnPaths.some((pathRule) => {
            if (typeof pathRule === 'function') {
                return pathRule(location.pathname);
            }

            if (pathRule instanceof RegExp) {
                return pathRule.test(location.pathname);
            }

            if (typeof pathRule === 'string') {
                return location.pathname === pathRule || location.pathname.startsWith(`${pathRule}/`);
            }

            return false;
        });
    });

    if (allAlerts.length === 0) {
        return null;
    }

    return (
        <div className={`grow-0 pb-4 ${compactTopSpacing ? 'pt-0' : 'pt-2'} flex flex-col gap-2 ${className}`.trim()}>
            {allAlerts.map((alert) => (
                <div key={alert.id} className={'alert-wrap'}>
                    <Banner2
                        type={alert.type ?? 'info'}
                        open={alert.open ?? true}
                        setOpen={alert.setOpen ?? (() => null)}
                        showClose={alert.showClose ?? false}
                        className={alert.className}
                        radiusClassName={bannerRadiusClassName}
                    >
                        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="min-w-0 flex-1 leading-6">
                                {alert.title ? <div className="font-semibold">{alert.title}</div> : null}
                                <div className={alert.title ? 'mt-1' : ''}>{alert.content}</div>
                            </div>
                            {alert.action ? <div className="flex shrink-0 justify-end sm:ml-auto">{alert.action}</div> : null}
                        </div>
                    </Banner2>
                </div>
            ))}
        </div>
    );
}
